/**
 * TikTok-Inspired Modern JavaScript
 * Enhanced interactions and TikTok embeds for Marketing Site
 */

class TikTokModern {
  constructor() {
    this.init();
  }

  init() {
    this.setupScrollAnimations();
    this.setupTikTokEmbeds();
    this.setupHoverEffects();
    this.setupNumberCounters();
    this.setupSmoothScrolling();
    this.setupParallaxEffects();
    this.setupLoadingStates();
    this.setupVideoAutoplay();
  }

  // Scroll-triggered animations
  setupScrollAnimations() {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, observerOptions);

    // Observe all elements with fade-in class
    document.querySelectorAll('.fade-in').forEach(el => {
      observer.observe(el);
    });

    // Stagger animations for grid items
    document.querySelectorAll('.grid .card').forEach((card, index) => {
      card.style.transitionDelay = `${index * 0.1}s`;
      card.classList.add('fade-in');
      observer.observe(card);
    });
  }

  // TikTok video embed functionality
  setupTikTokEmbeds() {
    // Load TikTok embed script if not already loaded
    if (!document.querySelector('script[src*="tiktok.com/embed"]')) {
      const script = document.createElement('script');
      script.src = 'https://www.tiktok.com/embed.js';
      script.async = true;
      document.head.appendChild(script);
    }

    // Handle lazy loading of TikTok embeds
    const tiktokEmbeds = document.querySelectorAll('.tiktok-embed');
    const embedObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.loadTikTokEmbed(entry.target);
          embedObserver.unobserve(entry.target);
        }
      });
    });

    tiktokEmbeds.forEach(embed => {
      embedObserver.observe(embed);
    });
  }

  // Load individual TikTok embed
  loadTikTokEmbed(container) {
    const videoUrl = container.dataset.videoUrl;
    if (!videoUrl) return;

    // Extract video ID from URL
    const videoId = this.extractTikTokVideoId(videoUrl);
    if (!videoId) return;

    // Create TikTok embed HTML
    const embedHtml = `
      <blockquote 
        class="tiktok-embed" 
        cite="${videoUrl}" 
        data-video-id="${videoId}"
        data-embed-from="embed_page"
      >
        <section>
          <a target="_blank" title="TikTok" href="${videoUrl}">View on TikTok</a>
        </section>
      </blockquote>
    `;

    container.innerHTML = embedHtml;

    // Trigger TikTok embed script
    if (window.tiktokEmbed) {
      window.tiktokEmbed.load();
    }
  }

  // Extract TikTok video ID from URL
  extractTikTokVideoId(url) {
    const patterns = [
      /tiktok\.com\/@[\w.-]+\/video\/(\d+)/,
      /tiktok\.com\/v\/(\d+)/,
      /tiktok\.com\/.*\/(\d+)/
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  }

  // Enhanced hover effects
  setupHoverEffects() {
    // Magnetic effect for buttons
    document.querySelectorAll('.btn-primary').forEach(btn => {
      btn.addEventListener('mousemove', (e) => {
        const rect = btn.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;
        
        btn.style.transform = `translate(${x * 0.1}px, ${y * 0.1}px) translateY(-2px)`;
      });

      btn.addEventListener('mouseleave', () => {
        btn.style.transform = 'translate(0, 0) translateY(0)';
      });
    });

    // Tilt effect for cards
    document.querySelectorAll('.card').forEach(card => {
      card.addEventListener('mousemove', (e) => {
        const rect = card.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width;
        const y = (e.clientY - rect.top) / rect.height;
        
        const tiltX = (y - 0.5) * 10;
        const tiltY = (x - 0.5) * -10;
        
        card.style.transform = `perspective(1000px) rotateX(${tiltX}deg) rotateY(${tiltY}deg) translateY(-4px)`;
      });

      card.addEventListener('mouseleave', () => {
        card.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) translateY(0px)';
      });
    });
  }

  // Animated number counters
  setupNumberCounters() {
    const counters = document.querySelectorAll('.stat-number[data-count]');
    
    const counterObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.animateCounter(entry.target);
          counterObserver.unobserve(entry.target);
        }
      });
    });

    counters.forEach(counter => {
      counterObserver.observe(counter);
    });
  }

  // Animate individual counter
  animateCounter(element) {
    const target = parseInt(element.dataset.count);
    const duration = 2000; // 2 seconds
    const start = Date.now();
    const startValue = 0;

    const updateCounter = () => {
      const now = Date.now();
      const progress = Math.min((now - start) / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const current = Math.floor(startValue + (target - startValue) * easeOutQuart);
      
      element.textContent = this.formatNumber(current);
      
      if (progress < 1) {
        requestAnimationFrame(updateCounter);
      } else {
        element.textContent = this.formatNumber(target);
      }
    };

    requestAnimationFrame(updateCounter);
  }

  // Format numbers with commas or suffixes
  formatNumber(num) {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toLocaleString();
  }

  // Smooth scrolling for anchor links
  setupSmoothScrolling() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', (e) => {
        e.preventDefault();
        const target = document.querySelector(anchor.getAttribute('href'));
        
        if (target) {
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      });
    });
  }

  // Parallax effects for hero sections
  setupParallaxEffects() {
    const parallaxElements = document.querySelectorAll('.hero::before');
    
    if (parallaxElements.length === 0) return;

    let ticking = false;

    const updateParallax = () => {
      const scrolled = window.pageYOffset;
      const rate = scrolled * -0.5;

      parallaxElements.forEach(element => {
        element.style.transform = `translateY(${rate}px)`;
      });

      ticking = false;
    };

    const requestParallaxUpdate = () => {
      if (!ticking) {
        requestAnimationFrame(updateParallax);
        ticking = true;
      }
    };

    window.addEventListener('scroll', requestParallaxUpdate);
  }

  // Loading states for buttons
  setupLoadingStates() {
    document.querySelectorAll('.btn[data-loading]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        if (btn.classList.contains('loading')) {
          e.preventDefault();
          return;
        }

        btn.classList.add('loading');
        btn.setAttribute('aria-busy', 'true');
        
        const originalText = btn.textContent;
        btn.innerHTML = '<span class="loading"></span> Loading...';

        // Simulate loading (remove this in production)
        setTimeout(() => {
          btn.classList.remove('loading');
          btn.removeAttribute('aria-busy');
          btn.textContent = originalText;
        }, 2000);
      });
    });
  }

  setupVideoAutoplay() {
    // Setup autoplay for the secondary video in hero section
    const secondaryVideo = document.querySelector('.lp-video__el--secondary');
    const playButton = document.querySelector('.lp-play');
    
    if (secondaryVideo) {
      // Set video attributes for autoplay
      secondaryVideo.setAttribute('autoplay', '');
      secondaryVideo.setAttribute('muted', '');
      secondaryVideo.setAttribute('loop', '');
      secondaryVideo.setAttribute('playsinline', '');
      
      // Hide controls completely
      secondaryVideo.removeAttribute('controls');
      secondaryVideo.style.setProperty('&::-webkit-media-controls', 'display: none !important');
      
      // Hide the play button since video will autoplay
      if (playButton) {
        playButton.style.display = 'none';
      }
      
      // Force play when in viewport
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            secondaryVideo.play().catch(() => {
              // Video autoplay prevented by browser - show play button as fallback
              if (playButton) {
                playButton.style.display = 'block';
              }
            });
          }
        });
      }, { threshold: 0.3 });
      
      observer.observe(secondaryVideo);
    }
  }

  // Utility: Debounce function
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Utility: Throttle function
  throttle(func, limit) {
    let inThrottle;
    return function() {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }
}

// TikTok Video Player Component
class TikTokVideoPlayer {
  constructor(container, options = {}) {
    this.container = container;
    this.options = {
      autoplay: false,
      muted: true,
      loop: true,
      ...options
    };
    this.init();
  }

  init() {
    this.createPlayerHTML();
    this.setupControls();
    this.setupIntersectionObserver();
  }

  createPlayerHTML() {
    const videoUrl = this.container.dataset.videoUrl;
    if (!videoUrl) return;

    this.container.innerHTML = `
      <div class="tiktok-player">
        <div class="tiktok-video-wrapper">
          <div class="tiktok-embed-placeholder" data-video-url="${videoUrl}">
            <div class="tiktok-loading">
              <div class="loading"></div>
              <p>Loading TikTok video...</p>
            </div>
          </div>
        </div>
        <div class="tiktok-controls">
          <button class="play-pause-btn" aria-label="Play/Pause">
            <svg class="play-icon" viewBox="0 0 24 24" width="24" height="24">
              <path d="M8 5v14l11-7z"/>
            </svg>
            <svg class="pause-icon" viewBox="0 0 24 24" width="24" height="24" style="display: none;">
              <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
            </svg>
          </button>
          <button class="mute-btn" aria-label="Mute/Unmute">
            <svg class="volume-icon" viewBox="0 0 24 24" width="24" height="24">
              <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/>
            </svg>
          </button>
        </div>
      </div>
    `;
  }

  setupControls() {
    const playPauseBtn = this.container.querySelector('.play-pause-btn');
    const muteBtn = this.container.querySelector('.mute-btn');

    if (playPauseBtn) {
      playPauseBtn.addEventListener('click', () => this.togglePlayPause());
    }

    if (muteBtn) {
      muteBtn.addEventListener('click', () => this.toggleMute());
    }
  }

  setupIntersectionObserver() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.loadVideo();
          observer.unobserve(entry.target);
        }
      });
    });

    observer.observe(this.container);
  }

  loadVideo() {
    const placeholder = this.container.querySelector('.tiktok-embed-placeholder');
    if (placeholder) {
      // This would integrate with TikTok's embed API
      // For now, we'll show the embed code
      const videoUrl = placeholder.dataset.videoUrl;
      const videoId = this.extractVideoId(videoUrl);
      
      if (videoId) {
        placeholder.innerHTML = `
          <blockquote 
            class="tiktok-embed" 
            cite="${videoUrl}" 
            data-video-id="${videoId}"
          >
            <section>
              <a target="_blank" href="${videoUrl}">View on TikTok</a>
            </section>
          </blockquote>
        `;

        // Load TikTok embed script
        if (window.tiktokEmbed) {
          window.tiktokEmbed.load();
        }
      }
    }
  }

  extractVideoId(url) {
    const match = url.match(/video\/(\d+)/);
    return match ? match[1] : null;
  }

  togglePlayPause() {
    // Implementation depends on TikTok embed API
    console.log('Toggle play/pause');
  }

  toggleMute() {
    // Implementation depends on TikTok embed API
    console.log('Toggle mute');
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Initialize main TikTok Modern functionality
  window.tiktokModern = new TikTokModern();

  // Initialize TikTok video players
  document.querySelectorAll('.tiktok-video-player').forEach(container => {
    new TikTokVideoPlayer(container);
  });

  // Add performance monitoring
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      console.log('TikTok Modern theme initialized successfully');
    });
  }
});

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { TikTokModern, TikTokVideoPlayer };
}